import React, { useEffect, useRef, useState } from "react";
import api from "../../../api/axios";
import { requestRoute } from "./TmapRouteFetcher";
import truckIcon from "../../../assets/icons/markers/truck.png";
import StartIcon from "../../../assets/icons/markers/factory.png";
import DepartIcon from "../../../assets/icons/markers/construction.png";

const TmapRouteViewer = () => {
  const mapRef = useRef(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [routeTable, setRouteTable] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [markers, setMarkers] = useState([]);
  const [polylines, setPolylines] = useState([]);

  const isTableReady =
    vehicleData && routeTable.length === vehicleData.trucks.length;

  // 차량 정보 불러오기
  useEffect(() => {
    api
      .get("stock/vehicle/")
      .then((res) => {
        setVehicleData(res.data);
      })
      .catch((err) => console.error("차량 정보 불러오기 실패", err));
  }, []);

  // 초기 지도 + 경로 요청
  useEffect(() => {
    if (vehicleData && window.Tmapv2) {
      const { centerX, centerY, trucks } = vehicleData;
      const map = new window.Tmapv2.Map("map_div", {
        center: new window.Tmapv2.LatLng(centerY, centerX),
        width: "100%",
        height: "500px",
        zoom: 8,
        zoomControl: true,
        scrollwheel: true,
      });
      mapRef.current = map;

      let tableData = [];

      // 모든 트럭에 대해 경로 요청
      trucks.forEach((truck, idx) => {
        const color = ["red", "blue", "green", "orange"][idx % 4];
        requestRoute(truck, map, color, (info) => {
          tableData.push({
            id: truck.truck_id,
            label: truck.label,
            totalDistance: info.distance / 1000, // km
            totalTime: info.time / 60, // min
            remainDistance: info.distance / 1000,
            remainTime: info.time / 60,
          });
          setRouteTable([...tableData]);
        });

        // 출발지 마커
        new window.Tmapv2.Marker({
          position: new window.Tmapv2.LatLng(truck.startY, truck.startX),
          map: map,
          title: truck.label,
          icon: StartIcon,
        });

        // 목적지 마커
        new window.Tmapv2.Marker({
          position: new window.Tmapv2.LatLng(truck.endY, truck.endX),
          map,
          title: "공사장",
          icon: DepartIcon,
        });
      });
    }
  }, [vehicleData]);

  // 3. 새로운 경로 요청 (버튼 클릭 시)
  const handleNewRoute = async () => {
    if (isLoading) {
      alert("🚧 새 경로를 요청 중입니다. 잠시만 기다려주세요.");
      return;
    }

    if (!isTableReady) {
      alert(
        "📊 경로 테이블이 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요."
      );
      return;
    }

    setIsLoading(true);
    try {
      const gpsRes = await api.get("stock/vehicle/?type=new");
      const { trucks } = gpsRes.data;
      if (!mapRef.current || !trucks) return;

      // 기존 폴리라인/마커 제거
      if (polylines.length > 0) {
        polylines.forEach((line) => {
          if (line && typeof line.setMap === "function") {
            line.setMap(null); // 지도에서 제거
          }
        });
        setPolylines([]); // 배열 초기화
      }
      if (markers.length > 0) {
        markers.forEach((m) => m.setMap(null));
        setMarkers([]);
      }

      const newMarkers = [];
      const newPolylines = [];
      const updatedTable = [...routeTable];

      for (let idx = 0; idx < trucks.length; idx++) {
        const truck = trucks[idx];
        const color = ["blue", "purple", "cyan", "orange"][idx % 4];

        // 새 경로 요청 후 그려진 선들을 받아 저장
        const lines = await requestRoute(
          truck,
          mapRef.current,
          color,
          (info) => {
            const i = updatedTable.findIndex((t) => t.id === truck.truck_id);
            if (i !== -1) {
              updatedTable[i] = {
                ...updatedTable[i],
                remainDistance: info.distance / 1000,
                remainTime: info.time / 60,
              };
            }
          }
        );

        if (Array.isArray(lines) && lines.length > 0) {
          newPolylines.push(...lines);
        }

        // 현위치 마커 추가
        const marker = new window.Tmapv2.Marker({
          position: new window.Tmapv2.LatLng(truck.startY, truck.startX),
          map: mapRef.current,
          title: `${truck.label} 현재위치`,
          icon: truckIcon,
          iconSize: new window.Tmapv2.Size(50, 50),
        });
        newMarkers.push(marker);
      }

      setPolylines(newPolylines);
      setMarkers(newMarkers);
      setRouteTable(updatedTable); // 테이블 갱신
    } catch (err) {
      console.error("새 경로 요청 중 오류:", err);
      alert("새 경로 요청에 실패했습니다. 다시 시도해 주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1rem",
        padding: "1rem",
      }}
    >
      {/* 지도 */}
      <div id="map_div" style={{ width: "100%", height: "500px" }} />

      {/* 우측 패널 */}
      <div>
        <button
          className={`btn-blue mb-4 ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handleNewRoute}
          disabled={isLoading}
        >
          {isLoading ? "⏳ 경로 요청 중..." : "🚛 새 경로 요청"}
        </button>
        <table className="min-w-full border border-gray-300 text-sm text-center">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">트럭 ID</th>
              <th className="border px-2 py-1">출발지</th>
              <th className="border px-2 py-1">총 거리(km)</th>
              <th className="border px-2 py-1">총 소요시간(분)</th>
              <th className="border px-2 py-1">남은 거리(km)</th>
              <th className="border px-2 py-1">남은 시간(분)</th>
            </tr>
          </thead>
          <tbody>
            {routeTable.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-2 text-gray-400">
                  경로 계산 중...
                </td>
              </tr>
            ) : (
              routeTable.map((truck) => (
                <tr key={truck.id}>
                  <td className="border px-2 py-1">{truck.id}</td>
                  <td className="border px-2 py-1">{truck.label}</td>
                  <td className="border px-2 py-1">
                    {truck.totalDistance?.toFixed(1)}
                  </td>
                  <td className="border px-2 py-1">
                    {Math.round(truck.totalTime)}
                  </td>
                  <td className="border px-2 py-1 text-blue-600">
                    {truck.remainDistance?.toFixed(1)}
                  </td>
                  <td className="border px-2 py-1 text-blue-600">
                    {Math.round(truck.remainTime)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TmapRouteViewer;
